import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import CookieService from '@/services/CookieService';
import { EUserTokenNames } from '@/types/user';

const getAccessToken = () => CookieService.getCookie('accessToken') || '';

const TENANT_ID = 'mindtech';
const LOGIN_URL = '/login';
const NOT_AUTH_CODE = 401;

export const publicHeaders = (): Record<string, string> => ({
  'X-Skip-Auth': '1',
  'X-Tenant-ID': TENANT_ID,
});

const isSkipAuthRequest = (args: string | FetchArgs): boolean => {
  if (typeof args === 'string') {
    return false;
  }

  const h = args.headers as HeadersInit | undefined;
  if (!h) {
    return false;
  }

  if (h instanceof Headers) {
    return h.get('X-Skip-Auth') === '1';
  }
  if (Array.isArray(h)) {
    return h.some(([k, v]) => k.toLowerCase() === 'x-skip-auth' && v === '1');
  }

  return (h as Record<string, string>)['X-Skip-Auth'] === '1';
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/v1/',
  prepareHeaders: (headers) => {
    headers.set('X-Tenant-ID', TENANT_ID);

    const skipAuth = headers.get('X-Skip-Auth') === '1';
    if (skipAuth) {
      headers.delete('X-Skip-Auth');

      return headers;
    }

    const accessToken = getAccessToken();
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return headers;
  },
});

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const skipAuth = isSkipAuthRequest(args);

  let result = await baseQuery(args, api, extraOptions);

  if (!skipAuth && result.error && result.error.status === NOT_AUTH_CODE) {
    try {
      const refreshResult = await baseQuery(
        {
          url: 'auth/refresh-access-token',
          method: 'POST',
          headers: publicHeaders(),
          credentials: 'include',
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        const { accessToken } = refreshResult.data as { accessToken?: string };

        if (accessToken) {
          CookieService.setCookie(EUserTokenNames.accessToken, accessToken);
        }

        result = await baseQuery(args, api, extraOptions);
      } else {
        CookieService.deleteCookie(EUserTokenNames.accessToken);
        window.location.href = LOGIN_URL;
      }
    } catch {
      CookieService.deleteCookie(EUserTokenNames.accessToken);
      window.location.href = LOGIN_URL;
    }
  }

  return result;
};
